import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { UserContextProvider, useCurrentUser, isOnyxFC } from './context/UserContext';

// Pages
import Dashboard from './pages/Dashboard';
import RequestsList from './pages/RequestsList';
import NewRequest from './pages/NewRequest';
import RequestDetail from './pages/RequestDetail';
import PayeesList from './pages/PayeesList';
import PayeeForm from './pages/PayeeForm';
import FundPools from './pages/FundPools';
import NewAllocation from './pages/NewAllocation';
import Admin from './pages/Admin';

// ─── Route guard: Onyx FC only ────────────────────────────────────────────────

function OnyxRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useCurrentUser();
  if (loading) return null;
  if (!isOnyxFC(user)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// ─── Unauthenticated splash — triggers MSAL redirect ─────────────────────────

function SignInRedirect() {
  const { instance, accounts } = useMsal();
  React.useEffect(() => {
    if (accounts.length === 0) {
      instance.loginRedirect({ scopes: ['openid', 'profile'] }).catch(console.error);
    }
  }, [instance, accounts.length]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-gray-500 text-sm">Redirecting to sign in...</p>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AuthenticatedTemplate>
        <UserContextProvider>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/requests" element={<RequestsList />} />
            <Route path="/requests/new" element={<NewRequest />} />
            <Route path="/requests/:id" element={<RequestDetail />} />
            <Route path="/payees" element={<PayeesList />} />
            <Route
              path="/payees/new"
              element={<OnyxRoute><PayeeForm mode="new" /></OnyxRoute>}
            />
            <Route
              path="/payees/:id/edit"
              element={<OnyxRoute><PayeeForm mode="edit" /></OnyxRoute>}
            />
            <Route path="/allocations" element={<FundPools />} />
            <Route
              path="/allocations/new"
              element={<OnyxRoute><NewAllocation /></OnyxRoute>}
            />
            <Route
              path="/admin"
              element={<OnyxRoute><Admin /></OnyxRoute>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </UserContextProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInRedirect />
      </UnauthenticatedTemplate>
    </BrowserRouter>
  );
}
